import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';

import {
  getActiveBinding,
  makeBorderStyles,
  makeContainerVisualEffectStyles,
} from '@blaze-shared/elements-domain';
import { safeDetectChanges } from '@blaze-shared/shared-ui';
import { toInt } from '@blaze-shared/shared-util';

import { ContainerActionsService } from '../../services/container-actions.service';
import { CanvasElementBaseComponent } from '../__base/base.component';

@Component({
  selector: 'be-video-player',
  templateUrl: './video-player.component.html',
  styleUrls: ['./video-player.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class VideoPlayerComponent
  extends CanvasElementBaseComponent<'videoPlayer'>
  implements OnChanges
{
  containerActionsService = inject(ContainerActionsService);

  @Input() value?: string;

  isPlaying = false;
  hasEnded = false;

  labelEnabled = false;
  labelText = '';
  sectionStyles: Record<string, string> = {};
  wrapperStyles: Record<string, string> = {};
  videoStyles: Record<string, string> = {};

  get isStaticVideoSource(): boolean {
    return !!this.props?.content?.videoSource?.enabled && !this.isVideoBound;
  }

  get isVideoBound(): boolean {
    return !!getActiveBinding(this.props?.data)?.dataSource;
  }

  get videoUrl(): string | undefined {
    if (this.isStaticVideoSource) {
      return this.props?.content?.videoSource?.url;
    } else {
      return this.value;
    }
  }

  constructor() {
    super();
    this.initMarginHandler((props) => props?.style?.video?.offset?.margin);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.props) {
      this.labelEnabled = this.props?.content.title.enabled ?? false;
      this.labelText = this.props?.content.title.label ?? '';
      this.updateStyles();
    }

    if (changes.value) {
      this.updateStyles();
    }
  }

  canPlay(): void {
    this.hasEnded = false;
  }

  playing(): void {
    this.isPlaying = true;
    const { enabled, workflowId } = this.props?.content.actions.play ?? {};
    if (enabled && workflowId) {
      this.containerActionsService.triggerWorkflow(this.output, undefined, undefined, workflowId);
    }
  }

  paused(): void {
    this.isPlaying = false;
  }

  ended(): void {
    this.isPlaying = false;
    this.hasEnded = true;
  }

  videoClicked(event: MouseEvent): void {
    if ('feature abandoned'.length) {
      return;
    }
    if (!this.props?.content.options.hideControls) {
      return;
    }
    const video = event.target as HTMLVideoElement;
    if (this.hasEnded) {
      video.currentTime = 0;
      video.play();
      this.hasEnded = false;
    } else if (this.isPlaying) {
      video.pause();
    } else {
      video.play();
    }
  }

  private updateStyles(): void {
    const { style, content } = this.props || {};
    const { title } = content || {};
    const { general, outline, video, labelBlock } = style || {};
    if (!style || !content || !general || !outline || !title || !video || !labelBlock) {
      return;
    }

    const sectionStyles: Record<string, string> = {};
    const wrapperStyles: Record<string, string> = {
      aspectRatio: content.options.aspectRatio,
    };

    const videoStyles: Record<string, string> = {
      ...makeBorderStyles(outline.color, outline.width),
      ...makeContainerVisualEffectStyles(video.visualEffect),
    };

    if (labelBlock.position === 'top') {
      sectionStyles['flex-direction'] = 'column';
    } else if (content?.title.enabled && labelBlock.width) {
      wrapperStyles['flex-basis'] = `${Math.max(100 - toInt(labelBlock.width, 100), 0)}%`;
    }

    if (outline.radius !== '0px') {
      videoStyles['border-radius'] = outline.radius;
    }

    this.wrapperStyles = wrapperStyles;
    this.sectionStyles = sectionStyles;
    this.videoStyles = videoStyles;
    safeDetectChanges(this.cdr);
  }
}
